import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Search, Filter, RefreshCw, ChevronRight, Clock,
  UserCheck, AlertTriangle, CheckCircle2, Pencil, Trash2, Send,
  RotateCcw, Ban, LogIn, Zap, UserX, ShieldCheck, ShieldOff, KeyRound,
} from 'lucide-react';
import api from '../services/api';

/* ── Action metadata ─────────────────────────────────────────────────────── */
const ACTION_META = {
  // Auth / users
  user_login:             { icon: LogIn,         color: 'text-purple-600 bg-purple-50 border-purple-200',  dot: 'bg-purple-500' },
  user_created:           { icon: UserCheck,     color: 'text-emerald-600 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  user_updated:           { icon: Pencil,        color: 'text-blue-600 bg-blue-50 border-blue-200',        dot: 'bg-blue-500' },
  // User access
  user_provisioned:       { icon: UserCheck,     color: 'text-emerald-600 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  user_enabled:           { icon: UserCheck,     color: 'text-emerald-600 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  user_disabled:          { icon: UserX,         color: 'text-red-600 bg-red-50 border-red-200',           dot: 'bg-red-500' },
  role_changed:           { icon: KeyRound,      color: 'text-blue-600 bg-blue-50 border-blue-200',        dot: 'bg-blue-500' },
  permissions_changed:    { icon: KeyRound,      color: 'text-blue-600 bg-blue-50 border-blue-200',        dot: 'bg-blue-400' },
  admin_granted:          { icon: ShieldCheck,   color: 'text-rose-600 bg-rose-50 border-rose-200',        dot: 'bg-rose-500' },
  admin_revoked:          { icon: ShieldOff,     color: 'text-rose-600 bg-rose-50 border-rose-200',        dot: 'bg-rose-400' },
  login_denied:           { icon: Ban,           color: 'text-amber-600 bg-amber-50 border-amber-200',     dot: 'bg-amber-500' },
  // Invoices
  invoice_created:        { icon: CheckCircle2,  color: 'text-emerald-600 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  invoice_sent:           { icon: Send,          color: 'text-blue-600 bg-blue-50 border-blue-200',        dot: 'bg-blue-500' },
  invoice_updated:        { icon: Pencil,        color: 'text-blue-600 bg-blue-50 border-blue-200',        dot: 'bg-blue-400' },
  invoice_voided:         { icon: Ban,           color: 'text-red-600 bg-red-50 border-red-200',           dot: 'bg-red-600' },
  // Payments
  payment_reversed:       { icon: RotateCcw,     color: 'text-amber-600 bg-amber-50 border-amber-200',     dot: 'bg-amber-500' },
  payment_recorded:       { icon: CheckCircle2,  color: 'text-emerald-600 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  // Bank txns
  bank_transaction_reversed: { icon: RotateCcw,  color: 'text-amber-600 bg-amber-50 border-amber-200',     dot: 'bg-amber-500' },
  // Deletions
  deleted:                { icon: Trash2,        color: 'text-red-600 bg-red-50 border-red-200',           dot: 'bg-red-500' },
};

const getActionMeta = (type = '') => {
  if (ACTION_META[type]) return ACTION_META[type];
  if (type.includes('reversed') || type.includes('voided')) return ACTION_META.payment_reversed;
  if (type.includes('deleted'))   return ACTION_META.deleted;
  if (type.includes('created'))   return ACTION_META.invoice_created;
  if (type.includes('sent'))      return ACTION_META.invoice_sent;
  if (type.includes('updated') || type.includes('paid')) return ACTION_META.invoice_updated;
  if (type.includes('login'))     return ACTION_META.user_login;
  return { icon: Zap, color: 'text-slate-600 bg-slate-100 border-slate-200', dot: 'bg-slate-400' };
};

const ENTITY_TYPES = [
  { value: '',                    label: 'All entities' },
  { value: 'invoice',            label: 'Invoices' },
  { value: 'payment',            label: 'Payments' },
  { value: 'bank_transaction',   label: 'Bank Transactions' },
  { value: 'auth',               label: 'Auth / Login' },
  { value: 'user',               label: 'Users' },
  { value: 'client',             label: 'Clients' },
  { value: 'contact',            label: 'Contacts' },
  { value: 'bank_account',       label: 'Bank Accounts' },
];

const ACTION_TYPES = [
  { value: '',                         label: 'All actions' },
  { value: 'invoice_voided',           label: 'Invoice Voided' },
  { value: 'payment_reversed',         label: 'Payment Reversed' },
  { value: 'bank_transaction_reversed',label: 'Bank Txn Reversed' },
  { value: 'invoice_created',          label: 'Invoice Created' },
  { value: 'invoice_sent',             label: 'Invoice Sent' },
  { value: 'user_login',               label: 'User Login' },
  { value: 'login_denied',             label: 'Sign-in Denied' },
  { value: 'user_provisioned',         label: 'User Provisioned' },
  { value: 'user_disabled',            label: 'User Disabled' },
  { value: 'user_enabled',             label: 'User Enabled' },
  { value: 'role_changed',             label: 'Role Changed' },
  { value: 'permissions_changed',      label: 'Permissions Changed' },
  { value: 'admin_granted',            label: 'Admin Granted' },
  { value: 'admin_revoked',            label: 'Admin Revoked' },
];

const fmtDateTime = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return s; }
};

const relTime = (s) => {
  if (!s) return '';
  try {
    const diff = Date.now() - new Date(s).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)   return 'just now';
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30)  return `${d}d ago`;
    return fmtDateTime(s).split(',')[0];
  } catch { return ''; }
};

const inputCls = 'border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';

/* ── Entity link helper ──────────────────────────────────────────────────── */
const EntityLink = ({ entityType, entityId }) => {
  if (!entityId) return null;
  if (entityType === 'invoice' || entityType === 'payment') {
    const invoiceId = entityType === 'invoice' ? entityId : null;
    if (invoiceId) {
      return (
        <Link to={`/invoices/${invoiceId}`}
          className="ml-2 inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          data-testid={`audit-entity-link-${entityId}`}>
          View <ChevronRight size={10} />
        </Link>
      );
    }
  }
  return null;
};

export default function AuditLog() {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [users, setUsers]           = useState([]);
  const [view, setView]             = useState('timeline');  // 'timeline' | 'table'

  // Filters
  const [entityType, setEntityType]     = useState('');
  const [actionType, setActionType]     = useState('');
  const [userId, setUserId]             = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [search, setSearch]             = useState('');
  const [limit, setLimit]               = useState(100);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (entityType) params.set('entity_type', entityType);
    if (actionType) params.set('action_type', actionType);
    if (userId)     params.set('performed_by_user_id', userId);
    if (dateFrom)   params.set('date_from', dateFrom);
    if (dateTo)     params.set('date_to', dateTo);
    if (search)     params.set('search', search);
    params.set('limit', limit);
    try {
      const res = await api.get(`/api/audit-log?${params}`);
      setLogs(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [entityType, actionType, userId, dateFrom, dateTo, search, limit]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    api.get('/api/audit-log/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const clearFilters = () => {
    setEntityType(''); setActionType(''); setUserId('');
    setDateFrom(''); setDateTo(''); setSearch('');
    setLimit(100);
  };

  const hasFilters = entityType || actionType || userId || dateFrom || dateTo || search;

  // Group by day for timeline
  const grouped = logs.reduce((acc, log) => {
    const day = log.created_at ? log.created_at.slice(0, 10) : 'Unknown';
    if (!acc[day]) acc[day] = [];
    acc[day].push(log);
    return acc;
  }, {});

  const formatDay = (iso) => {
    if (!iso || iso === 'Unknown') return 'Unknown Date';
    try {
      const d = new Date(iso + 'T00:00:00');
      const today = new Date(); today.setHours(0,0,0,0);
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      if (d.getTime() === today.getTime()) return 'Today';
      if (d.getTime() === yesterday.getTime()) return 'Yesterday';
      return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  // Summary stats
  const reversalCount = logs.filter(l => l.action_type?.includes('reversed') || l.action_type?.includes('voided')).length;
  const uniqueUsers   = new Set(logs.map(l => l.performed_by_user_id).filter(Boolean)).size;

  return (
    <div className="p-6 fade-in max-w-5xl" data-testid="audit-log-page">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={20} className="text-indigo-900" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Audit Trail</h1>
          </div>
          <p className="text-sm text-slate-500">
            Complete tamper-evident ledger of all system actions — GAAP / IFRS compliant
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView(v => v === 'timeline' ? 'table' : 'timeline')}
            className="px-3 py-1.5 border border-slate-200 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="toggle-view-button"
          >
            {view === 'timeline' ? 'Table view' : 'Timeline view'}
          </button>
          <button onClick={fetchLogs} disabled={loading}
            className="px-3 py-1.5 bg-indigo-900 text-white text-xs font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50 flex items-center gap-1"
            data-testid="refresh-audit-button">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: 'Events shown',      value: logs.length,    color: 'text-slate-900' },
          { label: 'Reversals / Voids', value: reversalCount,  color: reversalCount > 0 ? 'text-amber-700' : 'text-slate-900' },
          { label: 'Active users',      value: uniqueUsers,    color: 'text-slate-900' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <p className="text-xs text-slate-500 mb-0.5">{label}</p>
            <p className={`text-xl font-bold font-mono ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={13} className="text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Filters</span>
          {hasFilters && (
            <button onClick={clearFilters} className="ml-auto text-xs text-red-500 hover:text-red-700 font-medium"
              data-testid="clear-filters-button">
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative col-span-2 md:col-span-1">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input placeholder="Search events…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900"
              data-testid="audit-search-input" />
          </div>
          <select value={entityType} onChange={e => setEntityType(e.target.value)}
            className={inputCls} data-testid="audit-entity-type-filter">
            {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={actionType} onChange={e => setActionType(e.target.value)}
            className={inputCls} data-testid="audit-action-type-filter">
            {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={userId} onChange={e => setUserId(e.target.value)}
            className={inputCls} data-testid="audit-user-filter">
            <option value="">All users</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className={inputCls} data-testid="audit-date-from" title="From date" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className={inputCls} data-testid="audit-date-to" title="To date" />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <select value={limit} onChange={e => setLimit(Number(e.target.value))}
            className={`${inputCls} w-32`} data-testid="audit-limit-select">
            {[50, 100, 250, 500].map(n => <option key={n} value={n}>Last {n}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <div className="space-y-4">{[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-3 h-3 rounded-full bg-slate-100 mt-1 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-100 rounded animate-pulse w-32" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-3/4" />
              </div>
            </div>
          ))}</div>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Shield size={32} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No audit events found</p>
          <p className="text-xs text-slate-400 mt-1">Try adjusting the filters above</p>
        </div>
      ) : view === 'timeline' ? (

        /* ── TIMELINE VIEW ─────────────────────────────────────────────── */
        <div className="space-y-6" data-testid="audit-timeline">
          {Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([day, dayLogs]) => (
            <div key={day}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Clock size={13} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{formatDay(day)}</span>
                </div>
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">{dayLogs.length} event{dayLogs.length !== 1 ? 's' : ''}</span>
              </div>

              <ol className="relative border-l-2 border-slate-200 space-y-0 ml-2">
                {dayLogs.map((log, idx) => {
                  const meta  = getActionMeta(log.action_type);
                  const IconC = meta.icon;
                  const isCritical = log.action_type?.includes('reversed') || log.action_type?.includes('voided');

                  return (
                    <li key={log.log_id || idx} className="mb-5 ml-5" data-testid={`audit-event-${log.log_id || idx}`}>
                      {/* Timeline dot */}
                      <span className={`absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full border-2 border-white ${meta.dot}`}>
                        <IconC size={10} className="text-white" />
                      </span>

                      <div className={`bg-white border rounded-lg p-3 ml-1 transition-shadow hover:shadow-sm ${isCritical ? 'border-amber-200' : 'border-slate-200'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${meta.color}`}>
                                {log.action_type}
                              </span>
                              <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">
                                {log.entity_type}
                              </span>
                              {log.entity_type === 'invoice' && (
                                <EntityLink entityType={log.entity_type} entityId={log.entity_id} />
                              )}
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed">{log.action_summary}</p>
                            {log.metadata && typeof log.metadata === 'object' && Object.keys(log.metadata).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Object.entries(log.metadata).filter(([k]) => !['reversal_note'].includes(k) || log.metadata[k]).map(([k, v]) => (
                                  v !== null && v !== undefined && v !== '' && (
                                    <span key={k} className="text-xs bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-600">
                                      {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </span>
                                  )
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-mono text-slate-400 whitespace-nowrap">{relTime(log.created_at)}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {log.performed_by_system
                                ? <span className="text-slate-400">System</span>
                                : log.performed_by_email || log.performed_by_user_id || '—'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>

      ) : (

        /* ── TABLE VIEW ───────────────────────────────────────────────── */
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="audit-log-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Time', 'Entity', 'Action', 'Summary', 'By'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((log, idx) => {
                const meta  = getActionMeta(log.action_type);
                return (
                  <tr key={log.log_id || idx} className="hover:bg-slate-50" data-testid={`audit-row-${idx}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                      <div>{fmtDateTime(log.created_at)}</div>
                      <div className="text-slate-400">{relTime(log.created_at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{log.entity_type}</span>
                        {log.entity_type === 'invoice' && (
                          <EntityLink entityType={log.entity_type} entityId={log.entity_id} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border ${meta.color}`}>
                        {log.action_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-sm">
                      <p className="truncate" title={log.action_summary}>{log.action_summary}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {log.performed_by_system
                        ? <span className="text-slate-400 italic">system</span>
                        : log.performed_by_email || log.performed_by_user_id || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
